import { Button } from "@9amhealth/shared";
import styled from "@emotion/styled";
import React, { FC, useState } from "react";
import { KnownProgram } from "src/constants/programs";
import Progress from "src/ui/components/Progress/Progress";
import TaskList from "src/ui/components/TaskList/TaskList";
import Translate from "src/ui/components/Translate/Translate";
import {
  glpContinuationTasksToShow,
  TaskKey
} from "src/state/TaskManagementBloc/TaskManagementBloc";
import { TasksContext } from "src/constants/tasksContext";
import { GLP_CONTINUATION_ARTICLES } from "src/constants/glpContinuationArticles";
import { AppPopup } from "../AppQueryPopups/AppQueryPopupsBloc";
import { translate } from "lib/blocs/TranslateionBloc";
import CollectionTitle from "../StyledComponents/CollectionTitle";
import { ArticleCard } from "../ArticleCard/ArticleCard";
import IconCross from "@9amhealth/shared/icons/IconCross";

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: 1rem;
  position: relative;
  height: 25.8rem;
  margin: -1.5rem -1.5rem 2rem -1.5rem;
`;

const HeaderImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -1;
`;

const HeaderImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.5) 100%
  );
`;

const HeaderTitle = styled.h1`
  font-size: var(--title-font-size, 2.5rem);
  color: var(--color-white);
`;

const HeaderContent = styled.div`
  position: relative;
  z-index: 1;
  padding: 1.5rem;
`;

const HeaderDescription = styled.p`
  font-size: 1rem;
  font-weight: 400;
  color: var(--color-white);
`;

const Container = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
`;

const ProgressWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  line-height: 1;
`;

const ArticlesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(9.75rem, 1fr));

  @media (max-width: 768px) {
    gap: 1rem;
    grid-template-columns: repeat(2, 1fr);
  }
`;

const CloseButton = styled.button`
  position: fixed;
  top: max(0.75rem, env(safe-area-inset-top, 0.75rem));
  right: 0.75rem;
  z-index: 10;

  /* Size & shape */
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  padding: 0;

  /* iOS 26 Liquid Glass */
  background: linear-gradient(
    145deg,
    rgba(120, 120, 128, 0.36) 0%,
    rgba(80, 80, 88, 0.22) 100%
  );
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.22);
  box-shadow:
    0 4px 16px rgba(0, 0, 0, 0.18),
    inset 1px 1px 0 rgba(255, 255, 255, 0.3),
    inset -1px -1px 0 rgba(0, 0, 0, 0.12);

  /* Icon */
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.95);

  cursor: pointer;
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease,
    background 0.15s ease;

  &::after {
    content: "";
    position: absolute;
    inset: -0.75rem;
  }

  svg {
    width: 0.625rem;
    height: 0.625rem;
    fill: currentColor;
    flex-shrink: 0;
  }

  &:hover {
    background: linear-gradient(
      145deg,
      rgba(120, 120, 128, 0.48) 0%,
      rgba(80, 80, 88, 0.32) 100%
    );
    box-shadow:
      0 6px 20px rgba(0, 0, 0, 0.22),
      inset 1px 1px 0 rgba(255, 255, 255, 0.45),
      inset -1px -1px 0 rgba(0, 0, 0, 0.14);
    transform: scale(1.06);
  }

  &:active {
    transform: scale(0.94);
    box-shadow:
      0 2px 8px rgba(0, 0, 0, 0.18),
      inset 1px 1px 0 rgba(255, 255, 255, 0.2);
  }
`;

const Counter = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
`;

const ProgressTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

interface Props {
  onClose: () => void;
}

export const GlpContinuationTasksWithoutContext: FC<Props> = ({ onClose }) => {
  const { terminalTasks, totalTasks } = React.useContext(TasksContext);

  const allDone = terminalTasks === totalTasks;

  return (
    <Container>
      <HeaderContainer>
        <CloseButton type="button" aria-label="Close" onClick={onClose}>
          <IconCross />
        </CloseButton>
        <HeaderImageOverlay />
        <HeaderImage
          src="https://cdn.sanity.io/images/g38rxyoc/production/47e44ceea71ba5557e55fcf75da61bdd15efd52f-2528x1696.jpg"
          alt=""
        />

        <HeaderContent>
          <HeaderTitle className="as-h1">
            <Translate noWrap msg="glpContinuation.header.title" />
          </HeaderTitle>
          <HeaderDescription className="as-body">
            <Translate noWrap msg="glpContinuation.header.description" />
          </HeaderDescription>
        </HeaderContent>
      </HeaderContainer>

      <ProgressTitleContainer>
        <CollectionTitle className="m-bottom-half">
          <Translate msg="glpContinuation.yourChecklist" />
        </CollectionTitle>

        <ProgressWrapper>
          <Progress
            percent={totalTasks === 0 ? 0 : terminalTasks / totalTasks}
          />

          <Counter>
            {terminalTasks}/{totalTasks}
          </Counter>
        </ProgressWrapper>
      </ProgressTitleContainer>

      <TaskList
        hiddenTitle
        showBadges
        descriptionBelowLabel
        filterVisibleTasks={(task) =>
          glpContinuationTasksToShow.includes(task.slug as TaskKey)
        }
        programs={[KnownProgram.ONBOARDING]}
        onTaskCompletionBehavior={AppPopup.glpContinuation}
      />
      <nine-spacer s="lg" />

      <CollectionTitle>
        <Translate msg="glpContinuation.documentsHelp" />
      </CollectionTitle>

      <ArticlesGrid>
        {GLP_CONTINUATION_ARTICLES.map((article) => (
          <ArticleCard
            key={article.translationKey}
            title={translate(article.translationKey)}
            url={article.url}
            imageAlt={translate(article.translationKey)}
            image={article.image}
          />
        ))}
      </ArticlesGrid>
      <nine-spacer s="lg" />
      {allDone && (
        <Button
          onPress={onClose}
          style={{
            margin: "0 auto"
          }}
        >
          Continue
        </Button>
      )}
    </Container>
  );
};

export const GlpContinuationTasks: FC<Props> = (props) => {
  const [terminalTasks, setTerminalTasks] = useState(0);
  const [totalTasks, setTotalTasks] = useState(0);

  return (
    <TasksContext.Provider
      value={{ terminalTasks, setTerminalTasks, totalTasks, setTotalTasks }}
    >
      <GlpContinuationTasksWithoutContext {...props} />
    </TasksContext.Provider>
  );
};
